import React, { useMemo, useState } from 'react';
import { IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupSwapIcon from '@mui/icons-material/SwapHoriz';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Card, CardBody, StatusPill, TableScroll } from '../../PanelComerciales.styles';
import SectionHeader from '../SectionHeader/SectionHeader';
import CapacityEditor from '../../editors/CapacityEditor';
import SpecialtyEditor from '../../editors/SpecialtyEditor';
import EditableCell from '../../editors/EditableCell';
import CommercialEditDrawer, { CommercialEditMode } from '../../drawers/CommercialEditDrawer';
import { Commercial, FlowTypeDef, ProductDef, effectiveLeads } from '../../PanelComerciales.types';

interface CommercialsTableProps {
    commercials: Commercial[];
    teamOptions: string[];
    products: ProductDef[];
    flowTypes: FlowTypeDef[];
    /**
     * Puede ser async: el handler del panel espera el PUT (unwrap) para
     * decidir el toast de éxito/error. El tipo admite la Promise explícita
     * para no pasar una async donde se declara void (typescript:S6544).
     */
    onCommercialUpdate: (next: Commercial, prev: Commercial) => void | Promise<void>;
}

const STATUS_VARIANT = {
    'Activo': 'success',
    'Inactivo': 'muted',
    'Bloqueado': 'danger',
} as const;

/** Un valor de un grupo numérico de la tabla (capacidad, deals). */
interface StatValue {
    value: number;
    title: string;
    color: string;
    strong?: boolean;
}

/** Grupo de valores numéricos separados por «|» con tooltip común. */
const StatGroup: React.FC<{ tooltip: string; values: StatValue[] }> = ({ tooltip, values }) => (
    <Tooltip title={tooltip} arrow>
        <span style={{ display: 'inline-flex', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
            {values.map((v, i) => (
                <React.Fragment key={v.title}>
                    {i > 0 && <span style={{ color: '#9CA3AF' }}>|</span>}
                    {v.strong ? (
                        <strong title={v.title} style={{ color: v.color }}>{v.value}</strong>
                    ) : (
                        <span title={v.title} style={{ color: v.color }}>{v.value}</span>
                    )}
                </React.Fragment>
            ))}
        </span>
    </Tooltip>
);

const CommercialsTable: React.FC<CommercialsTableProps> = ({
    commercials,
    teamOptions,
    products,
    flowTypes,
    onCommercialUpdate,
}) => {
    const [teamFilter, setTeamFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [search, setSearch] = useState<string>('');

    const [drawerMode, setDrawerMode] = useState<CommercialEditMode | null>(null);
    const [drawerCommercial, setDrawerCommercial] = useState<Commercial | null>(null);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        // Orden del listado: ACTIVOS primero, INACTIVOS al final. Los BLOQUEADOS
        // van en medio y SOLO son visibles cuando se filtra explícitamente por
        // 'Bloqueado' (ocultos por defecto para no saturar el listado operativo).
        const STATUS_RANK = { Activo: 0, Bloqueado: 1, Inactivo: 2 } as const;
        return commercials
            .filter((c) => {
                if (teamFilter && c.team !== teamFilter) return false;
                if (statusFilter) {
                    if (c.status !== statusFilter) return false;
                } else if (c.status === 'Bloqueado') {
                    return false;
                }
                if (term && !c.name.toLowerCase().includes(term)) return false;
                return true;
            })
            .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
    }, [commercials, teamFilter, statusFilter, search]);

    const counts = useMemo(
        () => ({
            Activo: commercials.filter((c) => c.status === 'Activo').length,
            Inactivo: commercials.filter((c) => c.status === 'Inactivo').length,
            Bloqueado: commercials.filter((c) => c.status === 'Bloqueado').length,
        }),
        [commercials],
    );

    const updateField = <K extends keyof Commercial>(c: Commercial, key: K, value: Commercial[K]) => {
        onCommercialUpdate({ ...c, [key]: value }, c);
    };

    const setDistribution = (
        c: Commercial,
        next: { products: Record<string, number>; flows: Record<string, number> },
    ) => {
        // Mantiene `specialty` (alias deprecado) sincronizado con `products`.
        onCommercialUpdate(
            { ...c, products: next.products, specialty: next.products, flows: next.flows },
            c,
        );
    };

    const openDrawer = (mode: CommercialEditMode, c: Commercial) => {
        setDrawerMode(mode);
        setDrawerCommercial(c);
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        setDrawerCommercial(null);
    };

    return (
        <Card>
            <SectionHeader
                title='Comerciales'
                subtitle={`${counts.Activo} activos · ${counts.Inactivo} inactivos · ${counts.Bloqueado} bloqueados`}
                icon={<PersonOutlineIcon style={{ color: '#1E7BFF' }} />}
                actions={
                    <div style={{ display: 'flex', gap: 8 }}>
                        <TextField
                            size='small'
                            placeholder='Buscar'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ width: 200 }}
                        />
                        <TextField
                            size='small'
                            select
                            value={teamFilter}
                            onChange={(e) => setTeamFilter(e.target.value)}
                            sx={{ width: 140 }}
                            label='Equipo'
                        >
                            <MenuItem value=''>Todos</MenuItem>
                            {teamOptions.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size='small'
                            select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ width: 140 }}
                            label='Estado'
                        >
                            <MenuItem value=''>Todos</MenuItem>
                            <MenuItem value='Activo'>Activo</MenuItem>
                            <MenuItem value='Inactivo'>Inactivo</MenuItem>
                            <MenuItem value='Bloqueado'>Bloqueado</MenuItem>
                        </TextField>
                    </div>
                }
            />
            <CardBody>
                <TableScroll>
                    <table>
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Equipo</th>
                                <th>Estado</th>
                                <th>Capacidad %</th>
                                <th>Capacidad (Autom. | Efectiva | Mes)</th>
                                <th>
                                    Deals pipedrive
                                    <br />
                                    <span style={{ fontWeight: 400, fontSize: 11, color: '#6B7280' }}>
                                        Total | Open | Closed | Won
                                    </span>
                                </th>
                                <th>Distribución (producto / flujo)</th>
                                <th>Meta</th>
                                <th>Ausencia</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id}>
                                    <td><strong>{c.name}</strong></td>
                                    <td>{c.team}</td>
                                    <td>
                                        <StatusPill $variant={STATUS_VARIANT[c.status]}>
                                            {c.status}
                                        </StatusPill>
                                    </td>
                                    <td>
                                        <CapacityEditor
                                            value={c.capacity}
                                            onChange={(next) => updateField(c, 'capacity', next)}
                                        />
                                    </td>
                                    <td>
                                        <StatGroup
                                            tooltip='Capacidad automática (scoring) | efectiva (autom. × boost%) | asignados este mes · cupo restante del mes = efectiva − asignados · calculado por el cronjob de scoring, no editable'
                                            values={[
                                                { value: c.calcLeads ?? 0, title: 'Capacidad automática (scoring)', color: '#6B7280' },
                                                { value: c.leadsEfectivos ?? effectiveLeads(c), title: 'Capacidad efectiva (autom. × boost%)', color: '#2563EB', strong: true },
                                                { value: c.asignadosMes ?? 0, title: 'Asignados este mes (cuota mensual consumida)', color: '#F59E0B' },
                                            ]}
                                        />
                                    </td>
                                    <td>
                                        <StatGroup
                                            tooltip='Total | Abiertos | Cerrados (LOST+CLOSED) | Ganados (WON) · alimentado por Pipedrive · no editable'
                                            values={[
                                                { value: c.totalDeals ?? 0, title: 'Total', color: '#111827', strong: true },
                                                { value: c.openDeals ?? 0, title: 'Open', color: '#10B981' },
                                                { value: c.closedDeals ?? 0, title: 'Closed (LOST+CLOSED, excluye ganados)', color: '#6B7280' },
                                                { value: c.wonDeals ?? 0, title: 'Won', color: '#8B5CF6' },
                                            ]}
                                        />
                                    </td>
                                    <td>
                                        <SpecialtyEditor
                                            value={c.products ?? c.specialty ?? {}}
                                            flowsValue={c.flows ?? {}}
                                            products={products}
                                            flowTypes={flowTypes}
                                            onChange={(next) => setDistribution(c, next)}
                                        />
                                    </td>
                                    <td>
                                        <EditableCell
                                            value={c.goal}
                                            currency
                                            onCommit={(next) => updateField(c, 'goal', next)}
                                        />
                                    </td>
                                    <td>{c.absence || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Tooltip title='Editar'>
                                            <IconButton size='small' onClick={() => openDrawer('full', c)}>
                                                <EditOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Cambiar equipo'>
                                            <IconButton size='small' onClick={() => openDrawer('team', c)}>
                                                <GroupSwapIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Ajustar meta'>
                                            <IconButton size='small' onClick={() => openDrawer('goal', c)}>
                                                <PaidOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                                        No hay comerciales que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </TableScroll>
            </CardBody>

            {drawerMode && drawerCommercial && (
                <CommercialEditDrawer
                    open={Boolean(drawerMode)}
                    mode={drawerMode}
                    commercial={drawerCommercial}
                    teamOptions={teamOptions}
                    onClose={closeDrawer}
                    onSave={(next) => {
                        onCommercialUpdate(next, drawerCommercial);
                        closeDrawer();
                    }}
                />
            )}
        </Card>
    );
};

export default CommercialsTable;
